import React, { useState, useEffect } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, IconButton, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from '@mui/material/CircularProgress'; // <-- Add this
import axios from "axios";
import { getAvailableProducts } from "./ProductCard";

// Header Component
const Components = ({ onAddProduct, highlightProduct }) => {
  const isMobileOrTab = useMediaQuery("(max-width:1024px)");
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true); // <-- Add loading state

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true); // Start loading
    try {
      const response = await axios.get("https://enzo-matr.onrender.com/api/products"); // Fetch from `products`
      const productsWithQuantity = response.data.map((product) => ({
        ...product,
        quantity: 0, // Initialize quantity to 0
      }));
      const availableProducts = getAvailableProducts(productsWithQuantity); // Filter available products
      setProducts(availableProducts);
      setFilteredProducts(availableProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
    setLoading(false); // End loading
  };

  const handleSearch = () => {
    const filtered = products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) || // Fix: Use `name` instead of `detail`
        product.price.toString().includes(searchQuery) ||
        (product.discount &&
          product.discount.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredProducts(filtered);
  };

  const handleCategoryClick = (categoryName) => {
    if (categoryName === "") {
      setFilteredProducts(products); // Show all products
    } else {
      const filtered = products.filter((product) =>
        product.category.toLowerCase() === categoryName.toLowerCase()
      );
      setFilteredProducts(filtered);
    }
  };

  const handleCountryClick = (countryName) => {
    if (!countryName) {
      setFilteredProducts(products); // Show all products
    } else {
      const filtered = products.filter((product) =>
        (product.state || "").toLowerCase() === countryName.toLowerCase()
      );
      setFilteredProducts(filtered);
    }
  };

  const updateProductQuantity = (index, newQuantity) => {
    setFilteredProducts((prevProducts) => {
      const updatedProducts = prevProducts.map((product, i) => {
        if (i === index) {
          const updatedProduct = { ...product, quantity: Math.min(newQuantity, product.count) };
          if (newQuantity > 0) {
            onAddProduct(updatedProduct); // Notify parent about the added product
          }
          return updatedProduct;
        }
        return product;
      });
      const selected = updatedProducts.filter((product) => product.quantity > 0);
      setSelectedProducts(selected); // Update selectedProducts state
      return updatedProducts;
    });
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) return;

    const invoiceData = {
      items: selectedProducts.map((product) => ({
        item: product.name,
        quantity: product.quantity,
        price: product.setprice,
        vat: product.vat,
        total: product.setprice * product.quantity,
      })),
      overallTotal: selectedProducts.reduce((sum, p) => sum + p.price * p.quantity, 0),
    };

    try {
      // Save the invoice
      const response = await axios.post("https://enzo-matr.onrender.com/invoices", invoiceData);

      // Update product counts in the backend
      await Promise.all(
        selectedProducts.map((product) =>
          axios.put(`https://enzo-matr.onrender.com/api/products/${product._id}/reduce-count`, {
            quantity: product.quantity,
          })
        )
      );

      if (response.data.invoiceNumber) {
        alert("Invoice saved successfully. Invoice Number: " + response.data.invoiceNumber);
      } else {
        alert("Invoice saved, but no invoice number received.");
      }

      fetchProducts();
      setSelectedProducts([]);
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  // Move highlighted product to top when prop changes
  useEffect(() => {
    if (highlightProduct && filteredProducts.length > 0) {
      const idx = filteredProducts.findIndex(p => p.name === highlightProduct);
      if (idx > 0) {
        const newList = [...filteredProducts];
        const [item] = newList.splice(idx, 1);
        setFilteredProducts([item, ...newList]);
      }
    }
  }, [highlightProduct]); // <-- Run when highlightProduct changes

  return (
    <Box sx={{
      display: "flex",
      flexDirection: isMobileOrTab ? "column" : "row",
      gap: 2,
      padding: 2,
      fontFamily: "Montserrat, sans-serif",
      height: isMobileOrTab ? "auto" : "50%",
      width: isMobileOrTab ? "98vw" : "960px",
      marginLeft: isMobileOrTab ? 0 : "470px",
      marginTop: isMobileOrTab ? 1 : "-302px"
    }}>
      {/* Left Side: Search + Product Table */}
      <Box
        sx={{
          flex: 2,
          backgroundColor: "#ffffff",
          borderRadius: "4px",
          padding: isMobileOrTab ? "8px" : "20px",
          boxShadow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: isMobileOrTab ? "auto" : "127%",
          marginTop: isMobileOrTab ? 0 : "-240px", // <--- Here!
          marginLeft: isMobileOrTab ? 4 : "0px",
          width: isMobileOrTab ? "88vw" : "auto"
        }}
      >
        {/* Search Bar */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", }}>
          <TextField variant="outlined" placeholder="Search Item" size="small" sx={{ fontFamily: "'Bricolage Grotesque', sans-serif", border: "1.5px solid black", borderRadius: "10px", width: isMobileOrTab ? "70vw" : "200px" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <IconButton
            sx={{
              bgcolor: "black",
              borderRadius: "8px",
              width: "35px",
              height: "37px",
              color: "white",

              "&:hover": { bgcolor: "gray" },
            }} onClick={handleSearch}>
            <SearchIcon sx={{ fontSize: "25px" }} />
          </IconButton>
          <Button variant="contained" sx={{
            backgroundColor: "#25bdbe", color: "#000",
            textTransform: "none", fontFamily: "cardo",
            fontWeight: "bold", fontSize: "20px",
            height: "40px", width: "100px",
            border: "1.6px solid black",
            borderRadius: "10px"
          }}
            onClick={() => handleCountryClick("India")}>
            India
          </Button>
          <Button variant="contained" sx={{
            backgroundColor: "#25bdbe", color: "#000",
            textTransform: "none", fontFamily: "cardo",
            fontWeight: "bold", fontSize: "20px",
            height: "40px", width: "100px",
            border: "1.6px solid black",
            borderRadius: "10px"
          }}
            onClick={() => handleCountryClick("Saudi")}>
            Saudi
          </Button>
          <Button variant="contained" sx={{
            backgroundColor: "black", color: "white",
            textTransform: "none", fontFamily: "cardo",
            fontWeight: "bold", fontSize: "20px",
            height: "40px", width: "100px",
            border: "1.6px solid black",
            borderRadius: "10px"
          }}
            onClick={() => {
              handleCountryClick("")
              setSearchQuery(""); // Clear search input
            }}>
            All
          </Button>
        </Box>

        {/* Product Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductTable products={filteredProducts} onUpdateQuantity={updateProductQuantity} />
        )}
      </Box>

      {/* Right Side: Category List */}
      <Box sx={{
        flex: 1,
        backgroundColor: "#ffffff",
        borderRadius: "4px",
        padding: isMobileOrTab ? "8px" : "20px",
        boxShadow: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        ml: isMobileOrTab ? 3.5 : 0 ,
        marginTop: isMobileOrTab ? 1 : "-240px",
        height: isMobileOrTab ? "auto" : "126%",
        width: isMobileOrTab ? "88vw" : "auto"
      }}>
        <CategoryList onCategoryClick={handleCategoryClick} />
      </Box>
    </Box>
  );
};

// Product Table Component
const ProductTable = ({ products = [], onUpdateQuantity }) => {
  const isMobileOrTab = useMediaQuery("(max-width:1024px)");

  return (
    <TableContainer
      sx={{
        fontFamily: "'Bricolage Grotesque', sans-serif",
        width: isMobileOrTab ? "100%" : "100%",
        overflow: "auto",
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": { display: "none" },
        maxHeight: isMobileOrTab ? "300px" : "none", // Set max height for mobile/tab
        marginLeft: isMobileOrTab ? "-12px" : 0, // Center table on mobile/tab
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          tableLayout: "fixed",
          width: isMobileOrTab ? "98vw" : "100%",
          minWidth: isMobileOrTab ? "98vw" : "auto",
          ml: isMobileOrTab ? 2 : 0,
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#000" }}>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Item</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Count</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Qty</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Discount</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Price</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>VAT</TableCell>
            <TableCell sx={{ color: "#fff", fontSize: "15px", padding: "4px", backgroundColor: "black" }}>Total</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {products.length > 0 ? (
            products.map((product, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: product.quantity > 0 ? "#e3f0ff" : "inherit",
                  transition: "background 0.2s"
                }}
              >
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>{product.name}</TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>{product.count}</TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginLeft: "-60px", height: "30px", padding: "10px" }}>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#20a4a5", color: "#fff", minWidth: "25px", fontSize: "12px", padding: "2px" }}
                      onClick={() => onUpdateQuantity(index, Math.max(0, product.quantity - 1))}
                    >-</Button>
                    <TextField
                      size="small"
                      sx={{
                        width: "75px",
                        marginLeft: "-0px",
                        textAlign: "center",
                        fontSize: "11px",
                        height: "35px",
                        // Hide spin buttons for number input
                        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      }}
                      value={product.quantity}
                      inputProps={{
                        style: { textAlign: "center" },
                        min: 0,
                        max: product.count,
                        type: "number"
                      }}
                      onChange={e => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        if (val < 0) val = 0;
                        if (val > product.count) val = product.count;
                        onUpdateQuantity(index, val);
                      }}
                    />
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#20a4a5", color: "#fff", minWidth: "25px", fontSize: "12px", padding: "2px" }}
                      onClick={() => onUpdateQuantity(index, product.quantity + 1)}
                      disabled={product.quantity >= product.count}
                    >+</Button>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>{product.setdiscount}</TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>{product.setprice}</TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>{product.vat}</TableCell>
                <TableCell sx={{ fontSize: "15px", padding: "4px" }}>Rs. {product.setprice * product.quantity}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} align="center">No Products Found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Category List Component
const CategoryList = ({ onCategoryClick }) => {
  const [categories, setCategories] = useState([]);
  const [searchCategory, setSearchCategory] = useState("");
  const isMobileOrTab = useMediaQuery("(max-width:1024px)"); // Add this line

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/categories");
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "360px",
        width: isMobileOrTab ? "86vw" : "100%", // Responsive width
        alignSelf: isMobileOrTab ? "center" : "stretch", // Center on mobile/tab
      }}
    >
      {/* Search bar */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          variant="outlined"
          placeholder="Search Categories"
          size="small"
          fullWidth
          sx={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            borderRadius: "10px",
            border: "1px solid black",
          }}
          value={searchCategory}
          onChange={(e) => setSearchCategory(e.target.value)}
        />
        <IconButton
          sx={{
            bgcolor: "black",
            borderRadius: "8px",
            width: "35px",
            height: "37px",
            color: "white",
            "&:hover": { bgcolor: "gray" },
          }}
        >
          <SearchIcon sx={{ fontSize: "25px" }} />
        </IconButton>
      </Box>
      {/* Category list with scroll */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {filteredCategories.map((category, index) => (
          <Button
            key={index}
            variant="contained"
            sx={{
              backgroundColor: "#20a4a5",
              color: "#fff",
              fontSize: "13px",
              padding: "5px",
              fontFamily: "caladea",
              textTransform: "none",
              border: "1px solid black",
              borderRadius: "10px",
            }}
            onClick={() => onCategoryClick(category.name)}
          >
            {category.name}
          </Button>
        ))}
      </Box>
      {/* View All button fixed at bottom */}
      <Button
        variant="contained"
        sx={{
          backgroundColor: "black",
          color: "white",
          fontSize: "13px",
          padding: "5px",
          fontFamily: "caladea",
          textTransform: "none",
          border: "1px solid black",
          borderRadius: "10px",
          mt: 0,
        }}
        onClick={() => {
          setSearchCategory("");
          onCategoryClick("");
        }}
      >
        View All
      </Button>
    </Box>
  );
};

export default Components;